import React from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const SettingsDemo = () => {
  const { settings, formatCurrency, formatDate, calculateTotalWithTax, calculateCommission } = useSettings();

  const sampleAmount = 50000;
  const sampleDate = new Date();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
        🔗 Paramètres Interconnectés - Démonstration
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Les paramètres configurés dans la section "Paramètres" sont automatiquement appliqués 
        dans toute l'application en temps réel.
      </Alert>

      <Grid container spacing={3}>
        {/* Company Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: '#1e40af' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Informations Entreprise
                </Typography>
              </Box>
              
              {/* Company Logo Display */}
              {settings.companyLogo && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={settings.companyLogo}
                    alt="Logo entreprise"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0'
                    }}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748b' }}>
                    Logo personnalisé actuel
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                </Box>
              )}
              
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Nom de l'entreprise"
                    secondary={settings.companyName}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Email"
                    secondary={settings.companyEmail}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Téléphone"
                    secondary={settings.companyPhone}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Adresse"
                    secondary={settings.companyAddress}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ mr: 1, color: '#059669' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Configuration Financière
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Devise par défaut"
                    secondary={
                      <Chip 
                        label={settings.defaultCurrency} 
                        size="small" 
                        color="primary" 
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Taux de TVA"
                    secondary={`${settings.taxRate}%`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Taux de commission"
                    secondary={`${settings.commissionRate}%`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Frais d'annulation"
                    secondary={formatCurrency(settings.cancellationFee)}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Calculations */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                💰 Calculs Financiers en Temps Réel
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                Exemple avec un montant de {formatCurrency(sampleAmount)}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#f8fafc', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Montant HT
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(sampleAmount)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#f0fdf4', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Montant TTC ({settings.taxRate}%)
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(calculateTotalWithTax(sampleAmount))}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#fefce8', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Commission ({settings.commissionRate}%)
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {formatCurrency(calculateCommission(sampleAmount))}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#fef2f2', 
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      TVA
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {formatCurrency(calculateTotalWithTax(sampleAmount) - sampleAmount)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* System Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LanguageIcon sx={{ mr: 1, color: '#7c3aed' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Paramètres Système
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Langue"
                    secondary={settings.language === 'fr' ? 'Français' : settings.language}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Format de date"
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          {settings.dateFormat}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Exemple: {formatDate(sampleDate)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Fuseau horaire"
                    secondary={settings.timeZone}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Security & Notifications */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: '#dc2626' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Sécurité & Notifications
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <NotificationsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Notifications Email"
                    secondary={
                      <Chip 
                        label={settings.emailNotifications ? 'Activées' : 'Désactivées'}
                        size="small"
                        color={settings.emailNotifications ? 'success' : 'default'}
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <NotificationsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Notifications Push"
                    secondary={
                      <Chip 
                        label={settings.pushNotifications ? 'Activées' : 'Désactivées'}
                        size="small"
                        color={settings.pushNotifications ? 'success' : 'default'}
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Timeout de session"
                    secondary={`${settings.sessionTimeout} minutes`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Authentification 2FA"
                    secondary={
                      <Chip 
                        label={settings.twoFactorAuth ? 'Activée' : 'Désactivée'}
                        size="small"
                        color={settings.twoFactorAuth ? 'success' : 'warning'}
                      />
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12}>
          <Alert severity="success">
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              ✅ Comment tester l'interconnexion
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              1. Allez dans <strong>Paramètres → Financier</strong> et changez la devise de "DZD" vers "EUR" ou "USD"
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              2. Modifiez le taux de TVA ou de commission
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              3. Visitez <strong>Dashboard</strong>, <strong>Finances</strong>, ou cette page de démonstration
            </Typography>
            <Typography variant="body2">
              4. Observez que tous les montants sont automatiquement mis à jour avec la nouvelle devise et les nouveaux calculs !
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsDemo; 